import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Typography,
  List,
  ListItem,
  Chip,
  Divider,
  ListItemText
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from "react-router-dom";
import { useApi } from "../api/useApi";
import WebStoriesIcon from '@mui/icons-material/WebStories';

const AdminListingPage = () => {
  const navigate = useNavigate();
  const { authFetch } = useApi();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [openLogDialog, setOpenLogDialog] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyerIds, setSelectedBuyerIds] = useState([]);
  const [selectText, setSelectText] = useState("")

  // NEW state for checkboxes
  const [sendByEmail, setSendByEmail] = useState(true);
  const [sendByWhatsApp, setSendByWhatsApp] = useState(false);

  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const selectAllBuyersForSend = () => {
    if (selectedBuyerIds.length === 0) {
      const buyerIds = buyers.map(buyer => buyer.id)
      setSelectedBuyerIds(buyerIds)
    } else {
      setSelectedBuyerIds([])
    }
  }

  const closeSendDialog = () => {
    setSendDialogOpen(false)
    setSelectedBuyerIds([])
  }

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/admin/listing`);
      if (!res.ok) throw new Error("Failed to fetch listings.");
      const data = await res.json();
      setListings(data);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  useEffect(() => {
    if (selectedBuyerIds.length === 0) {
      setSelectText("Select all")
    } else {
      setSelectText("Remove all")
    }
  }, [selectedBuyerIds])

  const handleDeleteClick = (listing) => {
    setListingToDelete(listing);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!listingToDelete) return;

    try {
      const res = await authFetch(`/admin/listing/${listingToDelete.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete listing");
      showSnackbar("Listing deleted successfully");
      setConfirmOpen(false);
      setListingToDelete(null);
      fetchListings();
    } catch (err) {
      showSnackbar(err.message, "error");
      setConfirmOpen(false);
      setListingToDelete(null);
    }
  };

  const handleSendClick = async (listing) => {
    setSelectedListing(listing);
    setSendDialogOpen(true);
    try {
      const res = await authFetch(`/admin/buyer`);
      if (!res.ok) throw new Error("Failed to fetch buyers");
      const data = await res.json();
      setBuyers(data);
    } catch (err) {
      showSnackbar(err.message, "error");
    }
  };

  const handleOpenMessageLog = () => {
    setOpenLogDialog(true)
  }

  const sendBuyers = async () => {
    if (!selectedListing) return;
    try {
      const payload = {
        listingId: selectedListing.id,
        buyerIds: selectedBuyerIds,
        email: sendByEmail,
        whatsapp: sendByWhatsApp
      }

      console.log("Sending payload ", payload)

      const res = await authFetch(`/admin/listing/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to send listing to buyers");
      showSnackbar("Listing sent to selected buyers!");
    } catch (err) {
      showSnackbar(err.message, "error");
    } finally {
      setSendDialogOpen(false);
      setSelectedBuyerIds([]);
      setSelectedListing(null);
      setSendByEmail(true);
      setSendByWhatsApp(false);
    }
  };

  const filteredListings = listings.filter((l) =>
    [l.car.make, l.car.model, l.car.variant, l.car.vin]
      .join(" ")
      .toLowerCase()
      .includes(filter.toLowerCase())
  );

  const paginatedListings = filteredListings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Top Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search listings"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/admin/listings/new")}
        >
          Create Listing
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete listing{' '}
          <strong>{listingToDelete?.car?.make} {listingToDelete?.car?.model}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLogDialog}
        onClose={() => setOpenLogDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Message logs</DialogTitle>
        <DialogContent dividers>
          {selectedListing?.messages?.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              No logs
            </Typography>
          ) : (
            <List disablePadding>
              {selectedListing?.messages?.map((log, idx) => (
                <Box key={log.id || idx}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {new Date(log.occurred).toLocaleString("da-DK")}
                          </Typography>
                          <Chip
                            size="small"
                            label={log.messageState}
                            color={log.messageState === "OK" ? "primary" : "error"}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {log.interactionType}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {log.description}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Email: {log.buyer.email} | WhatsApp: {log.buyer.whatsappPhone}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {idx < selectedListing?.messages?.length - 1 && <Divider component="li" />}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogDialog(false)}>Luk</Button>
        </DialogActions>
      </Dialog>

      {/* Send to Buyers Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={closeSendDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Listing to Buyers</DialogTitle>
        <DialogContent>
          {/* Multi‐select */}
          <TextField
            label="Select Buyers"
            select
            fullWidth
            SelectProps={{ multiple: true }}
            value={selectedBuyerIds}
            onChange={(e) => setSelectedBuyerIds(e.target.value)}
            sx={{ mb: 2 }}
          >
            {buyers.map((buyer) => (
              <MenuItem key={buyer.id} value={buyer.id}>
                {buyer.firstName} {buyer.lastName} – {buyer.email}
              </MenuItem>
            ))}
          </TextField>

          {/* Checkboxes */}
          <Box display="flex" gap={2}>
            <Button onClick={selectAllBuyersForSend}>{selectText}</Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendByEmail}
                  onChange={(e) => setSendByEmail(e.target.checked)}
                />
              }
              label="Send via Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendByWhatsApp}
                  onChange={(e) => setSendByWhatsApp(e.target.checked)}
                />
              }
              label="Send via WhatsApp"
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          {/* Lower-left icon */}
          <Tooltip title="Message logs">
            <IconButton onClick={() => handleOpenMessageLog()}>
              <WebStoriesIcon color="primary" />
            </IconButton>
          </Tooltip>


          {/* Right-aligned buttons */}
          <Box>

            <Button onClick={closeSendDialog}>Cancel</Button>
            <Button
              onClick={sendBuyers}
              color="primary"
              variant="contained"
              disabled={selectedBuyerIds.length === 0}
              sx={{ ml: 1 }}
            >
              Send
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Table View */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Make</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Variant</TableCell>
                  <TableCell>First Registration</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell align="center">On hold</TableCell>
                  <TableCell align="center">Visible</TableCell>
                  <TableCell align="center">Go to Listing</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedListings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell>{listing.car.make}</TableCell>
                    <TableCell>{listing.car.model}</TableCell>
                    <TableCell>{listing.car.variant || '-'}</TableCell>
                    <TableCell>{listing.car.firstRegistrationDate}</TableCell>
                    <TableCell>{listing.primaryAdmin.email}</TableCell>
                    <TableCell align="center">{listing.onHold ? <DoNotDisturbAltIcon /> : null}</TableCell>
                    <TableCell align="center">{listing.externalVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View as Buyer">
                        <IconButton onClick={() => navigate(`/buyer/listings/${listing.id}`)}>
                          <ExitToAppIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">

                      <Tooltip title="Send to Buyer">
                        <IconButton onClick={() => handleSendClick(listing)}>
                          <SendIcon color="primary" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Listing">
                        <IconButton onClick={() => navigate(`/admin/listings/edit/${listing.id}`)}>
                          <EditIcon color="primary" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete Listing">
                        <IconButton onClick={() => handleDeleteClick(listing)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Tooltip>

                    </TableCell>
                  </TableRow>
                ))}
                {paginatedListings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No listings found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredListings.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AdminListingPage;
